import React, { FC } from 'react';
import styled, { css } from 'styled-components';
import { ConfigProvider } from 'antd';

import colors from '~/constants/colors';
import Header from '~/layouts/Header';
import theme from '~/constants/theme';

import companyTypeBackground from '~/img/companyTypeBackground.jpg';
import companyInformationSuccessBackground from '~/img/companyInformationSuccessBackground.jpg';
import { ContextProvider } from '~/controllers/CompanyTypeContext';
import { Scrollbars } from '~/components';
import { getLayoutHeight, useHeaderHeight } from '~/hooks/useHeaderHeight';
import { useDirection } from '~/hooks/i18nHooks';

interface Props {
  image: 'default' | 'success';
}

const CompanyTypeLayout: FC<Props> = (props) => {
  const { image, children } = props;
  const headerHeight = useHeaderHeight({ wizardHeader: false });
  const dir = useDirection();

  return (
    <ConfigProvider componentSize="large">
      <ContextProvider>
        <Header />
        <Scrollbars style={getLayoutHeight(headerHeight)}>
          <CenterContainer>
            <Container className="CompanyTypeLayout" image={image} dir={dir} style={getLayoutHeight(headerHeight)}>
              <div className="CompanyTypeLayout-Body">
                <div className="CompanyTypeLayout-FormContainer">{children}</div>
              </div>
            </Container>
          </CenterContainer>
        </Scrollbars>
      </ContextProvider>
    </ConfigProvider>
  );
};

const Container = styled.div<Props>`
  display: flex;
  flex-direction: column;
  flex: 1;
  max-width: 97.7%;
  h1 {
    margin-top: 0;
    margin-bottom: 32px;
  }
  @media (max-width: ${theme.breakpoints.sm}px) {
    padding: 0 12px;
  }
  .CompanyTypeLayout-Body::after {
    content: '';
    position: absolute;
    min-width: 51.7%;
    min-height: -webkit-fill-available;
    z-index: 1;
    display: flex;
    background-color: ${colors.layoutBodyBackground};
    background-repeat: no-repeat;
    background-size: cover;
    background-position: center;
    border-radius: ${theme.containerBorderRadius}px;
    ${({ dir }) =>
      dir === 'rtl'
        ? css`
            left: 0%;
          `
        : css`
            right: 0%;
          `}
    ${({ image }) =>
      image === 'default'
        ? css`
            background-image: url(${companyTypeBackground});
          `
        : image === 'success'
        ? css`
            background-image: url(${companyInformationSuccessBackground});
          `
        : undefined}
    @media (max-width: ${theme.breakpoints.md}px) {
      content: none;
      border-radius: ${theme.borderRadiusSm}px;
      background-image: none;
    }
  }
  .CompanyTypeLayout-Body {
    display: flex;
    position: relative;
    height: calc(100% - 16px);
    border-radius: ${theme.containerBorderRadius}px;

    .CompanyTypeLayout-FormContainer {
      width: 50%;
      z-index: 2;
      min-width: 49.3%;
      background-color: ${colors.componentBackground};
      border-radius: ${theme.containerBorderRadius}px;
      box-shadow: rgba(102, 130, 158, 0.2) 2px 0px 15px 0px;
      padding: 40px;
      display: flex;
      justify-content: space-between;
      flex-direction: column;
      align-self: stretch;
      .ant-form-item,
      .ant-btn-block {
        max-width: 400px;
      }
      @media (max-width: ${theme.breakpoints.md}px) {
        width: 100%;
        min-width: auto;
        padding: 80px 40px;
      }
      @media (max-width: ${theme.breakpoints.sm}px) {
        width: 100%;
        min-width: auto;
        padding: 64px 24px;
      }
      @media (max-width: ${theme.breakpoints.xs}px) {
        width: 100%;
        min-width: auto;
        padding: 32px 16px;
      }
    }
  }
`;

const CenterContainer = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
  background-color: ${colors.layoutBodyBackground};
`;

export default CompanyTypeLayout;
